import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const BookmarksContext = createContext(null);

export function BookmarksProvider({ children }) {
  const { user } = useAuth() || {};
  const [savedIds, setSavedIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) {
      setSavedIds(new Set());
      setLoaded(true);
      return;
    }
    setLoaded(false);
    api.get("/me/bookmarks/ids")
      .then(({ data }) => setSavedIds(new Set(data)))
      .catch(() => setSavedIds(new Set()))
      .finally(() => setLoaded(true));
  }, [user?.id]);

  const isSaved = useCallback((id) => savedIds.has(id), [savedIds]);

  const toggle = useCallback(async (providerIdOrSlug, providerId) => {
    if (!user) return { needsAuth: true };
    const key = providerId || providerIdOrSlug;
    const wasSaved = savedIds.has(key);
    // optimistic
    const next = new Set(savedIds);
    if (wasSaved) next.delete(key); else next.add(key);
    setSavedIds(next);
    try {
      if (wasSaved) {
        await api.delete(`/me/bookmarks/${providerIdOrSlug}`);
      } else {
        await api.post(`/me/bookmarks/${providerIdOrSlug}`);
      }
      return { saved: !wasSaved };
    } catch {
      // rollback
      setSavedIds(savedIds);
      return { error: true };
    }
  }, [savedIds, user]);

  return (
    <BookmarksContext.Provider value={{ savedIds, isSaved, toggle, loaded }}>
      {children}
    </BookmarksContext.Provider>
  );
}

export const useBookmarks = () => useContext(BookmarksContext) || { isSaved: () => false, toggle: () => {}, loaded: true };
